import { Divider, MenuItem, Select, Typography } from '@material-ui/core';
import { Inline, useResponsiveValue, useUID } from '@superdispatch/ui';
import { ReactNode, useMemo } from 'react';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { isLoadStageComplete, LoadStage } from '../data/LoadDTO';
import {
  formatLoadOrderByField,
  isLoadOrderByField,
  LoadOrderByField,
} from '../data/LoadsPageParams';

function getOrderByKeys(
  stage: LoadStage,
  canDispatchToCarrier: boolean | undefined,
): LoadOrderByField[][] {
  const isPickedUp = isLoadStageComplete(stage, 'picked_up');
  const isDelivered = isLoadStageComplete(stage, 'delivered');

  const keys: LoadOrderByField[][] = [
    ['broker_name'],
    ['pickup_city', 'delivery_city'],
    ['pickup_state', 'delivery_state'],
    [
      isPickedUp ? 'pickedup_date' : 'pickup_date',
      isDelivered ? 'delivered_date' : 'delivery_date',
    ],
    ['superpay_status'],
  ];

  return !canDispatchToCarrier ? keys : [...keys, ['is_dispatched_to_carrier']];
}

interface LoadsPageFilterOrderByProps {
  stage: LoadStage;
  orderBy: null | LoadOrderByField;
  onChange: (orderBy: null | LoadOrderByField) => void;
}

export function LoadsPageFilterOrderBy({
  stage,
  orderBy,
  onChange,
}: LoadsPageFilterOrderByProps) {
  const uid = useUID();
  const { data: carrierSettings } = useCarrierSettings();
  const orderKeys = useMemo(
    () => getOrderByKeys(stage, carrierSettings?.can_dispatch_to_carrier),
    [carrierSettings?.can_dispatch_to_carrier, stage],
  );
  const isMobile = useResponsiveValue(true, false);

  return (
    <Select
      variant="outlined"
      displayEmpty={true}
      value={orderBy || ''}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <Inline space="xxsmall">
              <Typography color="textSecondary" variant="body1">
                Filter By
              </Typography>
              <Typography color="textSecondary"> Select</Typography>
            </Inline>
          );
        }

        return (
          <Inline space="xxsmall">
            <Typography color="textSecondary" variant="body1">
              Filter By
            </Typography>

            <Typography>
              {formatLoadOrderByField(selected as LoadOrderByField)}
            </Typography>
          </Inline>
        );
      }}
      fullWidth={isMobile}
      label={isMobile && 'Filter by'}
      inputProps={{ 'aria-label': 'Filter by', 'aria-labelledby': uid }}
      onChange={({ target: { value } }) => {
        if (isLoadOrderByField(value)) {
          onChange(value);
        } else {
          onChange(null);
        }
      }}
    >
      {orderKeys.reduce<ReactNode[]>((acc, fields, idx) => {
        if (idx === 0) {
          acc.push(
            <MenuItem key="reset-order-by" value="">
              None
            </MenuItem>,
          );
        }

        acc.push(<Divider key={`divider-${idx}`} />);

        fields.forEach((value) => {
          acc.push(
            <MenuItem key={value} value={value}>
              {formatLoadOrderByField(value)}
            </MenuItem>,
          );
        });

        return acc;
      }, [])}
    </Select>
  );
}
